import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import { z } from 'zod';
import { createApiResponseSchema } from '../../../utils';
import { partnerApiPath } from '../../apiPath';
import { partnerHttpClient } from '../../httpClient';

const PartnerCampaignTypeSchema = z
  .enum(['PROPOSAL', 'RECRUITMENT'])
  .describe('PartnerCampaignTypeSchema');

const PartnerCampaignPlatformSchema = z
  .enum(['MUSINSA', 'TWENTY_NINE_CM'])
  .describe('PartnerCampaignPlatformSchema');

export type PartnerCampaignPlatform = z.infer<typeof PartnerCampaignPlatformSchema>;

const GetCampaignSeedingDetailRequestSchema = z
  .object({
    campaignId: z.union([z.number(), z.string()]),
  })
  .describe('GetCampaignSeedingDetailRequestSchema');

export type GetCampaignSeedingDetailRequest = z.infer<typeof GetCampaignSeedingDetailRequestSchema>;

const CampaignDetailOptionValueSchema = z
  .object({
    valueId: z.number(),
    value: z.string(),
    allowed: z.boolean(),
  })
  .describe('CampaignDetailOptionValueSchema');

const CampaignDetailAllowedOptionSchema = z
  .object({
    optionName: z.string(),
    values: z.array(CampaignDetailOptionValueSchema),
  })
  .describe('CampaignDetailAllowedOptionSchema');

const CampaignDetailExtraOptionValueSchema = z
  .object({
    valueId: z.number(),
    name: z.string(),
    sequence: z.number(),
    allowed: z.boolean(),
  })
  .describe('CampaignDetailExtraOptionValueSchema');

const CampaignDetailExtraOptionSchema = z
  .object({
    extraOptionId: z.number(),
    name: z.string(),
    required: z.boolean(),
    sequence: z.number(),
    values: z.array(CampaignDetailExtraOptionValueSchema),
  })
  .describe('CampaignDetailExtraOptionSchema');

const CampaignDetailGoodsItemSchema = z
  .object({
    goodsNo: z.number(),
    goodsName: z.string(),
    imageUrl: z.string(),
    price: z.number(),
    allowedOptions: z.array(CampaignDetailAllowedOptionSchema),
    extraOptions: z.array(CampaignDetailExtraOptionSchema),
  })
  .describe('CampaignDetailGoodsItemSchema');

const CampaignDetailGoodsSlotSchema = z
  .object({
    slotNo: z.number(),
    slotLabel: z.string(),
    isRepresentative: z.boolean(),
    goods: z.array(CampaignDetailGoodsItemSchema),
  })
  .describe('CampaignDetailGoodsSlotSchema');

const CampaignProposalStatusSchema = z
  .enum(['ACTIVE', 'INACTIVE'])
  .describe('CampaignProposalStatusSchema');

export type CampaignProposalStatus = z.infer<typeof CampaignProposalStatusSchema>;

const CampaignRecruitmentProgressStatusSchema = z
  .enum(['BEFORE_RECRUITMENT', 'RECRUITING', 'IN_PROGRESS', 'COMPLETED'])
  .describe('CampaignRecruitmentProgressStatusSchema');

export type CampaignRecruitmentProgressStatus = z.infer<
  typeof CampaignRecruitmentProgressStatusSchema
>;

const CampaignDetailInfoSchema = z
  .object({
    campaignId: z.number(),
    campaignType: PartnerCampaignTypeSchema,
    brandId: z.string(),
    brandName: z.string(),
    shippingDeadlineGuide: z.string(),
    // 발송 마감일: shippingDeadlineDays = "선정일로부터 N영업일" 의 N(BE 정책 상수),
    // estimatedShippingDeadlineAt = 예상 발송 마감일("yyyy-MM-dd HH:mm:ss", 공휴일 폴백 시 null). (SID-4316)
    shippingDeadlineDays: z.number().nullable().optional(),
    estimatedShippingDeadlineAt: z.string().nullable().optional(),
    status: CampaignProposalStatusSchema.nullable().optional(),
    progressStatus: CampaignRecruitmentProgressStatusSchema.nullable().optional(),
    maxParticipants: z.number().nullable().optional(),
    recruitmentStartAt: z.string().nullable().optional(),
    recruitmentEndAt: z.string().nullable().optional(),
    announcementAt: z.string().nullable().optional(),
  })
  .describe('CampaignDetailInfoSchema');

const CampaignDetailContentGuideSchema = z
  .object({
    snsChannels: z.array(
      z.enum(['INSTAGRAM', 'YOUTUBE', 'TWITTER', 'TIKTOK', 'NAVER_BLOG', 'THREADS', 'SNAP']),
    ),
    contentTypes: z.array(z.enum(['IMAGE', 'SHORT_FORM', 'LONG_FORM', 'TEXT', 'BLOG_POST'])),
    requiredHashtags: z.array(z.string()),
    requestNote: z.string().nullable().optional(),
  })
  .describe('CampaignDetailContentGuideSchema');

const GetCampaignSeedingDetailResponseDataSchema = z
  .object({
    platform: PartnerCampaignPlatformSchema,
    title: z.string(),
    campaignInfo: CampaignDetailInfoSchema,
    goodsSlots: z.array(CampaignDetailGoodsSlotSchema),
    contentGuide: CampaignDetailContentGuideSchema,
  })
  .describe('GetCampaignSeedingDetailResponseDataSchema');

export type GetCampaignSeedingDetailResponseData = z.infer<
  typeof GetCampaignSeedingDetailResponseDataSchema
>;

const GetCampaignSeedingDetailResponseSchema = createApiResponseSchema(
  GetCampaignSeedingDetailResponseDataSchema,
);

export type GetCampaignSeedingDetailResponse = z.infer<
  typeof GetCampaignSeedingDetailResponseSchema
>;

export const getCampaignSeedingDetail = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetCampaignSeedingDetailRequestSchema),
    async ({ campaignId }: GetCampaignSeedingDetailRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.get<GetCampaignSeedingDetailResponse>(
          partnerApiPath.getCampaignSeedingDetail(campaignId),
        ),
      ),
    validateApiResponse(GetCampaignSeedingDetailResponseSchema),
  ),
);
